import { parseWithZod } from '@conform-to/zod'
import { type SEOHandle } from '@nasa-gcn/remix-seo'
import { AnimatePresence, motion } from 'framer-motion'
import { Img } from 'openimg/react'
import { useTranslation } from 'react-i18next'
import { data, Link } from 'react-router'
import { serverOnly$ } from 'vite-env-only/macros'
import { z } from 'zod'
import {
	QuantityStepper,
	useCartFetcher,
	useOptimisticCart,
} from '#app/components/cart-stepper.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { fadeInUp, staggerContainer, staggerItem } from '#app/utils/animations.ts'
import { commitCart, getCart } from '#app/utils/cart.server.ts'
import { prisma } from '#app/utils/db.server.ts'
import { getLocale } from '#app/utils/locale.server.ts'
import { getNoteImgSrc } from '#app/utils/misc.tsx'
import { buildMeta } from '#app/utils/seo.ts'
import { cartTotals, formatEur } from '#app/utils/shop-pricing.ts'
import {
	getSiteSettings,
	requireShopEnabled,
} from '#app/utils/site-settings.server.ts'
import { type Route } from './+types/shop.ts'

const CartActionSchema = z.discriminatedUnion('intent', [
	z.object({
		intent: z.literal('add-to-cart'),
		productId: z.string(),
		quantity: z.coerce.number().int().min(-99).max(99).default(1),
	}),
	z.object({
		intent: z.literal('update-quantity'),
		productId: z.string(),
		quantity: z.coerce.number().int().min(0).max(99),
	}),
	z.object({
		intent: z.literal('remove-from-cart'),
		productId: z.string(),
	}),
])

export const handle: SEOHandle & { i18n: string } = {
	i18n: 'common',
	// serverOnly$ keeps the site-settings (prisma) reference out of the client
	// bundle (handle is a shared export) — same pattern as beelden_.$title.tsx.
	getSitemapEntries: serverOnly$(async () => {
		const { shopEnabled } = await getSiteSettings()
		return shopEnabled ? [{ route: '/shop', priority: 0.8 as const }] : []
	}),
}

export async function loader({ request }: Route.LoaderArgs) {
	await requireShopEnabled()
	const language = await getLocale(request)
	const [rawProducts, cart] = await Promise.all([
		prisma.product.findMany({
			where: { active: true },
			orderBy: [{ sortOrder: 'asc' }, { createdAt: 'asc' }],
			select: {
				id: true,
				slug: true,
				type: true,
				name: true,
				description: true,
				priceCents: true,
				stock: true,
				imageObjectKey: true,
				imageAltText: true,
				noteId: true,
				translations: {
					where: { language },
					select: { name: true, description: true },
				},
			},
		}),
		getCart(request),
	])

	// Resolve product images in one batch: own upload wins, else the first
	// image of the Note the product derives from.
	const noteIds = [
		...new Set(
			rawProducts
				.filter((p) => !p.imageObjectKey && p.noteId)
				.map((p) => p.noteId!),
		),
	]
	const noteImages = noteIds.length
		? await prisma.noteImage.findMany({
				where: { noteId: { in: noteIds } },
				orderBy: { createdAt: 'asc' },
				select: { noteId: true, objectKey: true, altText: true },
			})
		: []
	const firstImageByNote: Record<
		string,
		{ objectKey: string; altText: string | null }
	> = {}
	for (const image of noteImages) {
		firstImageByNote[image.noteId] ??= {
			objectKey: image.objectKey,
			altText: image.altText,
		}
	}

	const products = rawProducts.map((product) => ({
		id: product.id,
		slug: product.slug,
		type: product.type,
		name: product.translations[0]?.name || product.name,
		description:
			product.translations[0]?.description || product.description,
		priceCents: product.priceCents,
		stock: product.stock,
		image: product.imageObjectKey
			? { objectKey: product.imageObjectKey, altText: product.imageAltText }
			: product.noteId
				? (firstImageByNote[product.noteId] ?? null)
				: null,
	}))

	return data({ products, cart })
}

export async function action({ request }: Route.ActionArgs) {
	await requireShopEnabled()
	const formData = await request.formData()
	const submission = parseWithZod(formData, { schema: CartActionSchema })
	if (submission.status !== 'success') {
		return data(
			{ result: submission.reply() },
			{ status: submission.status === 'error' ? 400 : 200 },
		)
	}

	const cart = await getCart(request)
	const { intent, productId } = submission.value

	if (intent === 'remove-from-cart') {
		delete cart[productId]
	} else {
		const product = await prisma.product.findFirst({
			where: { id: productId, active: true },
			select: { stock: true },
		})
		if (!product) {
			return data(
				{ result: submission.reply({ formErrors: ['Product niet gevonden'] }) },
				{ status: 404 },
			)
		}
		const current = cart[productId] ?? 0
		const requested =
			intent === 'add-to-cart'
				? current + submission.value.quantity
				: submission.value.quantity
		const max = product.stock === null ? 99 : Math.min(product.stock, 99)
		const next = Math.max(0, Math.min(requested, max))
		if (next === 0) delete cart[productId]
		else cart[productId] = next
	}

	return data(
		{ result: { status: 'success' as const } },
		{ headers: { 'set-cookie': await commitCart(request, cart) } },
	)
}

type ShopProduct = Route.ComponentProps['loaderData']['products'][number]

export default function ShopRoute({ loaderData }: Route.ComponentProps) {
	const { t } = useTranslation()
	const { products } = loaderData
	const cart = useOptimisticCart(loaderData.cart)

	const books = products.filter((p) => p.type === 'book')
	const beeldjes = products.filter((p) => p.type === 'beeldje')
	const postcards = products.filter((p) => p.type === 'postcard')

	const lines = products
		.filter((p) => (cart[p.id] ?? 0) > 0)
		.map((p) => ({
			type: p.type,
			unitPriceCents: p.priceCents,
			quantity: cart[p.id]!,
		}))
	const itemCount = lines.reduce((sum, line) => sum + line.quantity, 0)
	const totals = cartTotals(lines)

	return (
		<div data-testid="shop-container" className="pb-[160px]">
			{/* Header */}
			<motion.section
				className="pt-[120px] pb-12"
				initial="initial"
				whileInView="animate"
				viewport={{ once: true, amount: 0.15 }}
				variants={fadeInUp}
			>
				<div className="container">
					<div className="eyebrow mb-4">{t('shopEyebrow')}</div>
					<h1
						className="display mb-6"
						style={{ fontSize: 'clamp(48px, 8vw, 112px)', maxWidth: '14ch' }}
					>
						{t('shopTitle')}
					</h1>
					<p
						className="max-w-[52ch] text-base leading-relaxed"
						style={{ color: 'var(--ink-soft)' }}
					>
						{t('shopIntro')}
					</p>
				</div>
			</motion.section>

			{/* Book feature block */}
			{books.map((book) => (
				<motion.section
					key={book.id}
					className="pb-20"
					initial="initial"
					whileInView="animate"
					viewport={{ once: true, amount: 0.15 }}
					variants={fadeInUp}
				>
					<div className="container">
						<div className="eyebrow mb-6 border-t pt-8" style={{ borderColor: 'var(--line)' }}>
							{t('shopBookSection')}
						</div>
						<div className="grid items-start gap-12 md:grid-cols-[1.2fr_1fr]">
							<ProductImage product={book} priority />
							<div>
								<h2
									className="font-heading mb-4 text-3xl font-medium italic"
									style={{ letterSpacing: '-0.01em' }}
								>
									{book.name}
								</h2>
								{book.description ? (
									<p
										className="mb-6 max-w-[48ch] text-base leading-relaxed"
										style={{ color: 'var(--ink-soft)' }}
									>
										{book.description}
									</p>
								) : null}
								<div className="mb-6 text-xl font-medium tabular-nums">
									{formatEur(book.priceCents)}
								</div>
								<CartControls product={book} quantity={cart[book.id] ?? 0} />
							</div>
						</div>
					</div>
				</motion.section>
			))}

			{/* Beeldjes */}
			{beeldjes.length > 0 && (
				<section className="pb-20">
					<div className="container">
						<div
							className="eyebrow mb-8 border-t pt-8"
							style={{ borderColor: 'var(--line)' }}
						>
							{t('shopBeeldjesSection')}
						</div>
						<motion.div
							className="grid gap-10 sm:grid-cols-2 lg:grid-cols-3"
							initial="initial"
							whileInView="animate"
							viewport={{ once: true, amount: 0.1 }}
							variants={staggerContainer}
						>
							{beeldjes.map((product) => (
								<motion.article key={product.id} variants={staggerItem}>
									<ProductImage product={product} />
									<div className="flex items-baseline justify-between gap-3 pt-3">
										<h3 className="font-heading text-[17px] font-medium italic">
											{product.name}
										</h3>
										<span className="text-[15px] tabular-nums">
											{formatEur(product.priceCents)}
										</span>
									</div>
									{product.description ? (
										<p
											className="mt-1 text-[13px] leading-relaxed"
											style={{ color: 'var(--muted-gallery)' }}
										>
											{product.description}
										</p>
									) : null}
									<div className="mt-4">
										<CartControls
											product={product}
											quantity={cart[product.id] ?? 0}
										/>
									</div>
								</motion.article>
							))}
						</motion.div>
					</div>
				</section>
			)}

			{/* Postcards */}
			{postcards.length > 0 && (
				<section className="pb-20">
					<div className="container">
						<div
							className="mb-8 flex flex-wrap items-baseline justify-between gap-3 border-t pt-8"
							style={{ borderColor: 'var(--line)' }}
						>
							<span className="eyebrow">{t('shopPostcardsSection')}</span>
							<span
								className="rounded-full border px-3 py-1 text-[13px]"
								style={{ borderColor: 'var(--line)', color: 'var(--ink-soft)' }}
							>
								{t('shopPostcardBundleHint')}
							</span>
						</div>
						<motion.div
							className="grid grid-cols-2 gap-8 md:grid-cols-4"
							initial="initial"
							whileInView="animate"
							viewport={{ once: true, amount: 0.1 }}
							variants={staggerContainer}
						>
							{postcards.map((product) => (
								<motion.article key={product.id} variants={staggerItem}>
									<ProductImage product={product} />
									<div className="pt-3">
										<h3 className="font-heading text-[15px] font-medium italic">
											{product.name}
										</h3>
										<div
											className="mt-1 text-[13px] tabular-nums"
											style={{ color: 'var(--muted-gallery)' }}
										>
											{formatEur(product.priceCents)}
										</div>
									</div>
									<div className="mt-3">
										<QuantityStepper
											productId={product.id}
											slug={product.slug}
											quantity={cart[product.id] ?? 0}
										/>
									</div>
								</motion.article>
							))}
						</motion.div>
					</div>
				</section>
			)}

			{/* Sticky cart bar — the only cart UI */}
			<AnimatePresence>
				{itemCount > 0 && (
					<motion.div
						key="cart-bar"
						data-testid="cart-bar"
						initial={{ y: 96, opacity: 0 }}
						animate={{ y: 0, opacity: 1 }}
						exit={{ y: 96, opacity: 0 }}
						transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
						className="fixed z-40 flex flex-wrap items-center justify-between gap-4 px-6 py-4 md:px-10"
						style={{
							bottom: 'var(--frame-w)',
							left: 'var(--frame-w)',
							right: 'var(--frame-w)',
							background: 'rgba(250, 248, 244, 0.92)',
							backdropFilter: 'blur(14px)',
							WebkitBackdropFilter: 'blur(14px)',
							borderTop: '1px solid var(--line)',
						}}
					>
						<div className="flex flex-wrap items-baseline gap-x-6 gap-y-1">
							<span className="eyebrow">
								{t('shopCartItemCount', { count: itemCount })}
							</span>
							<span className="text-sm" style={{ color: 'var(--ink-soft)' }}>
								{t('shopSubtotal')}{' '}
								<span className="tabular-nums">
									{formatEur(totals.subtotalCents)}
								</span>
								{' · '}
								{t('shopShippingPostnl')}{' '}
								<span className="tabular-nums">
									{formatEur(totals.shippingCents)}
								</span>
							</span>
							<span className="text-base font-medium tabular-nums">
								{t('shopTotal')} {formatEur(totals.totalCents)}
							</span>
						</div>
						<Link
							to="/shop/afrekenen"
							className="btn-gallery"
							discover="none"
							data-testid="cart-checkout"
						>
							{t('shopCheckout')}
							<Icon name="arrow-right" className="h-4 w-4" />
						</Link>
					</motion.div>
				)}
			</AnimatePresence>
		</div>
	)
}

/**
 * Add / in-cart controls for single-add products (book, beeldjes). One click
 * adds; once in the cart the quantity is edited inline (or removed).
 */
function CartControls({
	product,
	quantity,
}: {
	product: ShopProduct
	quantity: number
}) {
	const { t } = useTranslation()
	// Shared cart fetcher: cart cookie writes must never run concurrently.
	const fetcher = useCartFetcher()
	const busy = fetcher.state !== 'idle'
	const soldOut = product.stock !== null && product.stock <= 0
	const max = product.stock === null ? 99 : Math.min(product.stock, 99)

	if (soldOut) {
		return (
			<button
				type="button"
				disabled
				className="btn-gallery btn-gallery-ghost"
				style={{ opacity: 0.5, cursor: 'not-allowed' }}
				data-testid={`shop-soldout-${product.slug}`}
			>
				{t('shopSoldOut')}
			</button>
		)
	}

	if (quantity <= 0) {
		return (
			<button
				type="button"
				className="btn-gallery"
				data-testid={`shop-add-${product.slug}`}
				disabled={busy}
				onClick={() =>
					fetcher.submit(
						{ intent: 'update-quantity', productId: product.id, quantity: '1' },
						{ method: 'POST', action: '/shop' },
					)
				}
			>
				<Icon name="plus" className="h-4 w-4" />
				{t('shopAddToCart')}
			</button>
		)
	}

	return (
		<div className="flex flex-wrap items-center gap-4">
			{max > 1 ? (
				<QuantityStepper
					productId={product.id}
					slug={product.slug}
					quantity={quantity}
					max={max}
				/>
			) : (
				<span
					className="inline-flex items-center gap-2 text-sm"
					data-testid={`shop-incart-${product.slug}`}
				>
					<Icon name="check" className="h-4 w-4" />
					{t('shopInCartLabel')}
				</span>
			)}
			<button
				type="button"
				className="text-[13px] underline underline-offset-4"
				style={{ color: 'var(--muted-gallery)', cursor: 'pointer' }}
				data-testid={`shop-remove-${product.slug}`}
				disabled={busy}
				onClick={() =>
					fetcher.submit(
						{ intent: 'remove-from-cart', productId: product.id },
						{ method: 'POST', action: '/shop' },
					)
				}
			>
				{t('shopRemoveFromCart')}
			</button>
		</div>
	)
}

function ProductImage({
	product,
	priority = false,
}: {
	product: ShopProduct
	priority?: boolean
}) {
	if (!product.image) {
		return (
			<div
				className="flex aspect-square w-full items-center justify-center"
				style={{ background: 'var(--paper-2)' }}
			>
				<Icon
					name="camera"
					className="h-10 w-10"
					style={{ color: 'var(--muted-gallery)' }}
				/>
			</div>
		)
	}
	return (
		<div
			className="overflow-hidden"
			style={{ background: 'var(--paper-2)' }}
		>
			<Img
				src={getNoteImgSrc(product.image.objectKey)}
				alt={product.image.altText ?? product.name}
				className="block h-auto w-full"
				width={600}
				height={600}
				fit="cover"
				isAboveFold={priority}
			/>
		</div>
	)
}

export const meta: Route.MetaFunction = () => {
	return buildMeta({
		title: 'Winkel',
		description:
			'Het boek van Else Ringnalda, kleine beelden en ansichtkaarten uit het atelier. Verzending via PostNL.',
		url: '/shop',
	})
}
